import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { showImage } from "../../../utils";
import Loading from "../utilComponent/Loading";

const Pricing = () => {
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]); 
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    per_page: 12,
    total: 0,
    last_page: 1,
  });

  const [savingId, setSavingId] = useState(null);

  const getMembers = async () => {
    try {
      const response = await request({
        url: "warehouse-members",
        method: "GET",
      });
      setMembers(response?.data || []);
    } catch (error) {
      toast.error("Failed to load members");
    }
  };

  useEffect(() => {
    getMembers();
  }, []);

  const fetchProducts = async (memberId, pageNo = 1) => {
    setLoading(true);
    try {
      const response = await request({
        url: `member-pricing/${memberId}`,
        method: "GET",
        params: {
          page: pageNo,
          per_page: pagination.per_page,
        },
      });

      if (response?.status) {
        setProducts(response.data.products || []);
        setFilteredProducts(response.data.products || []);
        setPagination(response.data.pagination || pagination);
      }
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleMemberChange = (memberId) => {
    setSelectedMember(memberId);
    setPage(1);
    setSearchQuery("");
    if (memberId) {
      fetchProducts(memberId, 1);
    } else {
      setProducts([]);
      setFilteredProducts([]);
    }
  };

  const handleDiscountChange = (id, value) => {
    const discount = Math.max(0, Math.min(100, Number(value)));

    setProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, discount } : p))
    );
    setFilteredProducts((prev) =>
      prev.map((p) => (p.id === id ? { ...p, discount } : p))
    );
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query) {
      setFilteredProducts(products);
    } else {
      const lowerQuery = query.toLowerCase();
      setFilteredProducts(
        products.filter((p) => p.name.toLowerCase().includes(lowerQuery))
      );
    }
  };

  const makeFormData = (product) => {
    const formData = new FormData();
    formData.append(`products[0][product_id]`, product.id);
    formData.append(`products[0][discount]`, product.discount);
    return formData;
  };

  const handleSave = async (product) => {
    if (product.discount < 0 || product.discount > 100) {
      toast.error("Discount must be between 0 and 100");
      return;
    }
    setSavingId(product.id);
    try {
      await request({
        url: `member-pricing/${selectedMember}`,
        method: "POST",
        data: makeFormData(product),
      });

      toast.success("Discount saved successfully");
    } catch (error) {
      toast.error("Failed to save discount");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="container-fluid p-0">
        <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
          <div className="container-fluid">
            <ol className="breadcrumb bg-transparent mb-0">
              <li className="breadcrumb-item">
                <Link className="text-secondary" to="/hrms">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item text-secondary">Pricing</li>
              <li className="breadcrumb-item active">Member Pricing</li>
            </ol>
          </div>
        </div>

        <div className="card mx-4">
          <div className="px-4 pt-3 pb-2">
            <label className="form-label fw-bold">Select Member</label>
            <select
              className="form-select mb-3"
              value={selectedMember}
              onChange={(e) => handleMemberChange(e.target.value)}
            >
              <option value="">-- Select Member --</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>

            {selectedMember && products.length > 0 && (
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            )}
          </div>

          <hr style={{ borderTop: "2px dotted gray" }} />
          <div className="row g-3 px-4 mt-2 mb-4">
            {loading && <Loading />}

            {!loading &&
              filteredProducts.map((p) => (
                <div className="col-xl-3 col-lg-4 col-md-6" key={p.id}>
                  <div className="card h-100">
                    <img
                      src={showImage(p.images[0].url)}
                      alt="Product"
                      className="card-img-top"
                      style={{
                        height: "180px",
                        objectFit: "cover",
                      }}
                    />

                    <div className="card-body d-flex flex-column">
                      <p className="mb-2">
                        <strong>Name:</strong> {p.name}
                      </p>
                      <p className="mb-2">
                        <strong>SKU:</strong> {p?.sku}
                      </p>
                      <p className="mb-2">
                        <strong>Price:</strong> ${p.price}
                      </p>

                      <label className="form-label">Discount %</label>
                      <input
                        type="number"
                        className="form-control mb-3"
                        min="0"
                        max="100"
                        value={p.discount ?? 0}
                        onChange={(e) =>
                          handleDiscountChange(p.id, e.target.value)
                        }
                      />

                      <button
                        className="btn btn-success mt-auto"
                        disabled={savingId === p.id}
                        onClick={() => handleSave(p)}
                      >
                        {savingId === p.id ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}

            {!loading && selectedMember && filteredProducts.length === 0 && (
              <div className="text-center py-4">
                <strong>No products found.</strong>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;
